import { useMemo, useState } from 'react';
import { SkeletonTable } from './ui.jsx';
import { t } from '../i18n/index.jsx';

/**
 * Lightweight native table — drop-in replacement for Syncfusion AppDataGrid.
 * Supports field-based sort, cell templates, and optional row action.
 */
export default function DataTable({
  data = [],
  columns = [],
  loading = false,
  onRowAction,
  actionLabel,
  onRowClick,
}) {
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  const sorted = useMemo(() => {
    if (!sortKey) return data;
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...data].sort((a, b) => {
      const av = a?.[sortKey];
      const bv = b?.[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return String(av).localeCompare(String(bv), undefined, { sensitivity: 'base', numeric: true }) * dir;
    });
  }, [data, sortKey, sortDir]);

  function toggleSort(col) {
    if (!col.field || col.sortable === false) return;
    if (sortKey === col.field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(col.field);
      setSortDir('asc');
    }
  }

  if (loading) return <SkeletonTable rows={6} cols={Math.min(columns.length + (onRowAction ? 1 : 0), 6)} />;

  return (
    <div className="table-wrap panel" style={{ padding: 0 }}>
      <table className="table data-table">
        <thead>
          <tr>
            {columns.map((col) => {
              const sortable = col.field && col.sortable !== false;
              const active = sortKey === col.field;
              return (
                <th
                  key={col.field || col.headerText}
                  style={col.width ? { width: col.width } : undefined}
                  className={sortable ? 'sortable' : undefined}
                  onClick={() => sortable && toggleSort(col)}
                  aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                >
                  <span className="th-inner">
                    {col.headerText}
                    {sortable && (
                      <span className={`sort-ind ${active ? 'on' : ''}`} aria-hidden>
                        {active ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}
                      </span>
                    )}
                  </span>
                </th>
              );
            })}
            {onRowAction && <th className="col-action" />}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => (
            <tr
              key={row.id ?? JSON.stringify(row)}
              className={onRowClick || onRowAction ? 'clickable' : undefined}
              onClick={() => onRowClick?.(row)}
            >
              {columns.map((col) => (
                <td key={col.field || col.headerText} style={col.textAlign ? { textAlign: col.textAlign } : undefined}>
                  {col.template
                    ? col.template(row)
                    : (row[col.field] != null && row[col.field] !== '' ? row[col.field] : '—')}
                </td>
              ))}
              {onRowAction && (
                <td className="col-action" onClick={(e) => e.stopPropagation()}>
                  <button type="button" className="btn ghost sm" onClick={() => onRowAction(row)}>
                    {actionLabel || t('Open')}
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
